import { useCallback, useEffect, useRef, useState } from "react";
import { Space, message } from "antd";
import { CloseOutlined, RightOutlined, ClockCircleOutlined } from "@ant-design/icons";
import CustomButton from "../../../components/CustomButton";
import { useNavigate, useParams } from "react-router-dom";
import { formatMetadataDict, getCustomerDetail, getOrderDetail, createBasicOrder, createOrder, convertEmptyStringToNull, OrderStatus, type CustomerDTO } from "@ifa/utils";
import { FnaSection, type FnaSectionHandle } from "./components/fna/FnaSection";
import { CustomerProfileSection, type CustomerProfileHandle } from "./components/profile/CustomerProfileSection";
import { InsuredSection, type InsuredSectionHandle } from "./components/insured/InsuredSection";
import { BeneficiarySection, type BeneficiarySectionHandle } from "./components/beneficiary/BeneficiarySection";
import { HistoryOrderSection, type HistoryOrderSectionHandle } from "./components/history/HistoryOrderSection";
import { ZuuCustomerDetail, ZuuCustomerDetailView, ZuuModeSwitch } from "./components/zuu";
import styles from "./index.module.css";
import dayjs from "dayjs";
import { getCustomerDetailSectionMode, type CustomerNavKey } from "./zuuMode.js";

const navItems = [
  { key: "fna", label: "FNA" },
  { key: "profile", label: "客戶資料" },
  { key: "insured", label: "受保人" },
  { key: "beneficiary", label: "受益人" },
  { key: "history", label: "歷史訂單" },
];

export function CustomerDetail() {
  const navigate = useNavigate();
  const { customerId } = useParams<{ customerId: string }>();
  const [customer, setCustomer] = useState<CustomerDTO | null>(null);
  const [activeNav, setActiveNav] = useState<string>(navItems[0].key);
  const [isZuuMode, setIsZuuMode] = useState(false);
  const [dirtyMap, setDirtyMap] = useState<Record<string, boolean>>({});
  const [editModeMap, setEditModeMap] = useState<Record<string, "view" | "edit">>({});
  const fnaRef = useRef<FnaSectionHandle>(null);
  const profileRef = useRef<CustomerProfileHandle>(null);
  const insuredRef = useRef<InsuredSectionHandle>(null);
  const beneficiaryRef = useRef<BeneficiarySectionHandle>(null);
  const historyRef = useRef<HistoryOrderSectionHandle>(null);
  const activeNavItem = navItems.find((item) => item.key === activeNav);
  const currentDirty = dirtyMap[activeNav] ?? false;
  const currentMode = editModeMap[activeNav] ?? "view";
  const supportsEdit = activeNav === "profile"; // 只有 profile 支持编辑模式
  const handleFnaDirtyChange = useCallback(
    (dirty: boolean) =>
      setDirtyMap((prev) => {
        if ((prev.fna ?? false) === dirty) return prev;
        return { ...prev, fna: dirty };
      }),
    []
  );
  const handleProfileDirtyChange = useCallback(
    (dirty: boolean) =>
      setDirtyMap((prev) => {
        if ((prev.profile ?? false) === dirty) return prev;
        return { ...prev, profile: dirty };
      }),
    []
  );

  useEffect(() => {
    setDirtyMap({});
    setEditModeMap({});
    setIsZuuMode(false);
  }, [customerId]);

  const refreshCustomer = useCallback(() => {
    if (!customerId) return;
    getCustomerDetail(customerId)
      .then((res) => {
        setCustomer(res);
      })
      .catch((err) => {
        console.error("Failed to fetch customer detail", err);
      });
  }, [customerId]);

  const handleNavChange = useCallback(
    (nextNav: string) => {
      if (nextNav === activeNav) return;
      if (currentDirty) {
        const proceed = window.confirm("當前有未儲存的內容，切換將會丟失。確認切換？");
        if (!proceed) return;
        setDirtyMap((prev) => ({ ...prev, [activeNav]: false }));
        setEditModeMap((prev) => ({ ...prev, [activeNav]: "view" }));
      }
      setActiveNav(nextNav);
      setEditModeMap((prev) => ({ ...prev, [nextNav]: "view" }));
    },
    [activeNav, currentDirty]
  );

  const handleZuuModeToggle = useCallback(() => {
    const anyDirty = Object.values(dirtyMap).some(Boolean);
    if (anyDirty) {
      const proceed = window.confirm("當前有未儲存的內容，切換模式將會丟失。確認切換？");
      if (!proceed) return;
      setDirtyMap({});
      setEditModeMap({});
    }
    setIsZuuMode((prev) => !prev);
  }, [dirtyMap]);

  const renderSharedSection = useCallback(
    (
      sectionMode:
        | "shared-profile"
        | "shared-insured"
        | "shared-beneficiary"
        | "shared-history"
    ) => {
      switch (sectionMode) {
        case "shared-profile":
          return (
            <CustomerProfileSection
              ref={profileRef}
              customer={customer}
              policyholder={customer?.policyholder}
              mode={currentMode}
              onDirtyChange={handleProfileDirtyChange}
              onSave={(policyholder) => {
                setCustomer((prev) => {
                  if (!prev) return prev;
                  const chineseName = `${policyholder.basic?.chineseSurname ?? ""}${policyholder.basic?.chineseGivenName ?? ""}`.trim();
                  const englishName = `${policyholder.basic?.englishSurname ?? ""} ${policyholder.basic?.englishGivenName ?? ""}`.trim();
                  const birthDate = policyholder.basic?.birthDate;
                  const age = birthDate ? dayjs().diff(dayjs(birthDate), "year") : prev.age;
                  return {
                    ...prev,
                    policyholder,
                    ChineseName: chineseName || prev.ChineseName,
                    englishName: englishName || prev.englishName,
                    idType: policyholder.basic?.idType ?? prev.idType,
                    idNumber: policyholder.basic?.idNumber ?? prev.idNumber,
                    birthDate: birthDate ?? prev.birthDate,
                    age,
                    updateTime: dayjs().format("YYYY-MM-DD HH:mm"),
                  };
                });
              }}
            />
          );
        case "shared-insured":
          return <InsuredSection ref={insuredRef} customer={customer} onRefresh={refreshCustomer} />;
        case "shared-beneficiary":
          return <BeneficiarySection ref={beneficiaryRef} customer={customer} onRefresh={refreshCustomer} />;
        case "shared-history":
          return <HistoryOrderSection ref={historyRef} customer={customer} onRefresh={refreshCustomer} />;
        default:
          return null;
      }
    },
    [
      currentMode,
      customer,
      handleProfileDirtyChange,
      refreshCustomer,
      setCustomer,
    ]
  );

  useEffect(() => {
    if (!customerId) return;
    let active = true;
    getCustomerDetail(customerId)
      .then((res) => {
        if (!active) return;
        setCustomer(res);
      })
      .catch((err) => {
        console.error("Failed to fetch customer detail", err);
      });
    return () => {
      active = false;
    };
  }, [customerId]);

  // 再次投保处理函数
  const handleReInsure = useCallback(async () => {
    if (!customer) return;

    // 检查是否有历史订单
    const latestOrder = customer.historyOrderList?.[0]; // 假设列表已按时间倒序排列

    if (latestOrder?.orderId) {
      // 基于最新订单再次投保
      const hideLoading = message.loading("正在創建新訂單...", 0);
      try {
        const detail: any = await getOrderDetail(latestOrder.orderId);
        const cleanData = {
          birthDate: detail.policyholder.basic.birthDate,
          chineseGivenName: detail.policyholder.basic.chineseGivenName,
          chineseSurname: detail.policyholder.basic.chineseSurname,
          englishGivenName: detail.policyholder.basic.englishGivenName,
          englishSurname: detail.policyholder.basic.englishSurname,
          forceAddOrder: detail.forceAddOrder,
          gender: detail.policyholder.basic.gender,
          idNumber: detail.policyholder.basic.idNumber,
          idType: detail.policyholder.basic.idType,
        };
        const payload = {
          ...cleanData,
          draft: true,
          submit: false,
        };

        if (detail.status === OrderStatus.Draft) {
          const res = await createBasicOrder(convertEmptyStringToNull(payload));
          if (res && res.orderId) {
            navigate(`/orders/${res.orderId}/edit?from=customer-detail&customerId=${customer.customerId}&initialStep=0`, {
              state: {
                orderStatus: OrderStatus.Draft,
              },
            });
          }
        } else {
          const createOrderPayload = {
            policyholder: detail.policyholder,
            insured: detail.insured,
            draft: true,
            submit: false,
          };
          if (
            createOrderPayload.policyholder &&
            detail.policyholder?.sourceId != null
          ) {
            createOrderPayload.policyholder.policyholderId =
              detail.policyholder.sourceId;
          }
          if (
            createOrderPayload.insured &&
            detail.insured?.sourceId != null
          ) {
            createOrderPayload.insured.insuredId = detail.insured.sourceId;
          }

          const res = await createOrder(convertEmptyStringToNull(createOrderPayload));
          if (res) {
            navigate(`/orders/${res}/edit?from=customer-detail&customerId=${customer.customerId}&initialStep=0`, {
              state: {
                orderStatus: OrderStatus.Draft,
              },
            });
          }
        }
      } catch (error) {
        console.error("Re-insure failed:", error);
        message.error("創建新訂單失敗");
      } finally {
        hideLoading();
      }
    } else {
      // 没有历史订单，基于客户信息创建新订单
      const hideLoading = message.loading("正在創建新訂單...", 0);
      try {
        // 从客户的 policyholder 信息构建订单数据
        const policyholder = customer.policyholder;
        if (!policyholder?.basic) {
          message.warning("客戶資料不完整，請先完善客戶資料");
          hideLoading();
          return;
        }

        const cleanData = {
          birthDate: policyholder.basic.birthDate,
          chineseGivenName: policyholder.basic.chineseGivenName,
          chineseSurname: policyholder.basic.chineseSurname,
          englishGivenName: policyholder.basic.englishGivenName,
          englishSurname: policyholder.basic.englishSurname,
          gender: policyholder.basic.gender,
          idNumber: policyholder.basic.idNumber,
          idType: policyholder.basic.idType,
          draft: true,
          submit: false,
        };

        const res = await createBasicOrder(convertEmptyStringToNull(cleanData));
        if (res && res.orderId) {
          navigate(`/orders/${res.orderId}/edit?from=customer-detail&customerId=${customer.customerId}&initialStep=0`, {
            state: {
              orderStatus: OrderStatus.Draft,
            },
          });
        }
      } catch (error) {
        console.error("Create order failed:", error);
        message.error("創建新訂單失敗");
      } finally {
        hideLoading();
      }
    }
  }, [customer, navigate]);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.breadcrumbRow}>
          <div className={styles.breadcrumb}>
            <div style={{ lineHeight: "22px", cursor: "pointer" }} onClick={() => navigate("/customers", { replace: true })}>
              客戶
            </div>
            <RightOutlined style={{ fontSize: "16px", color: "#94A3B8" }} />
            <div style={{ lineHeight: "22px", color: "#1E293B" }}>{activeNavItem?.label}</div>
          </div>
          <CustomButton
            icon={<CloseOutlined />}
            bgColor="transparent"
            style={{ color: "#94A3B8" }}
            onClick={() => {
              const anyDirty = Object.values(dirtyMap).some(Boolean);
              if (anyDirty) {
                const proceed = window.confirm("當前有未儲存的內容，離開將會丟失。確認關閉？");
                if (!proceed) return;
              }
              navigate(-1);
            }}
          />
        </div>

        <div className={styles.titleInfoRow}>
          <div className={styles.titleRow}>
            <div className={styles.nameBlock}>
              <div className={styles.nameZh}>{customer?.chineseName ?? "-"}</div>
              <div className={styles.nameEn}>{customer?.englishName ?? "-"}</div>
            </div>
            <div className={styles.updateTime}>
              <ClockCircleOutlined style={{ fontSize: 12, marginRight: 5 }} />
              更新時間：{customer?.updateTime ?? "-"}
            </div>
          </div>

          <div className={styles.infoStrip}>
            <div className={styles.infoPill}>
              <span className={styles.infoLabel}>{formatMetadataDict("ID_TYPE", customer?.idType)}</span>
              <span className={styles.infoValue}>{customer?.idNumber ?? "-"}</span>
            </div>
            <div className={styles.infoPill}>
              <span className={styles.infoLabel}>出生日期</span>
              <span className={styles.infoValue}>{customer?.birthDate ? `${customer.birthDate}${customer.age ? ` (${customer.age}歲)` : "-"}` : "-"}</span>
            </div>
            <div className={styles.infoPill}>
              <span className={styles.infoLabel}>手機號</span>
              <span className={styles.infoValue}>{customer?.policyholder?.personal?.phone ?? "-"}</span>
            </div>
            <div className={styles.infoPill} style={{ borderRight: "1px solid #e2e8f0" }}>
              <span className={styles.infoLabel}>顧問</span>
              <span className={styles.infoValue}>{customer?.advisorName ? `${customer.advisorName}${customer.iaCode ? ` (${customer.iaCode})` : ""}` : "-"}</span>
            </div>
            <Space className={styles.infoActions}>
              <CustomButton onClick={handleReInsure} bgColor="#fff" color="#666" style={{ border: "1px solid #d9d9d9" }}>
                再次投保
              </CustomButton>
            </Space>
          </div>
        </div>
      </div>

      {isZuuMode ? (
        <ZuuCustomerDetail
          customer={customer}
          activeNav={activeNav as CustomerNavKey}
          onNavigate={(nav) => handleNavChange(nav)}
          resolveSectionMode={(nav) =>
            getCustomerDetailSectionMode(true, nav)
          }
          renderDashboard={() => <ZuuCustomerDetailView customer={customer} />}
          renderSharedSection={renderSharedSection}
        />
      ) : (
        <div className={styles.body}>
          <div className={styles.sideNav}>
            {navItems.map((item) => (
              <div
                key={item.key}
                className={`${styles.navItem} ${activeNav === item.key ? styles.navItemActive : ""}`}
                onClick={() => handleNavChange(item.key)}
              >
                <span>{item.label}</span>
              </div>
            ))}
          </div>

          <div className={styles.content}>
            {activeNav === "fna" ? (
              <FnaSection ref={fnaRef} customer={customer} onDirtyChange={handleFnaDirtyChange} mode="view" />
            ) : (
              renderSharedSection(
                getCustomerDetailSectionMode(true, activeNav as CustomerNavKey) as
                  | "shared-profile"
                  | "shared-insured"
                  | "shared-beneficiary"
                  | "shared-history"
              )
            )}
          </div>
        </div>
      )}
      {/* 编辑按钮容器 */}
      {supportsEdit && (
        <div className={styles.editToolbar}>
          <Space>
            {currentMode === "view" ? (
              <CustomButton
                bgColor="var(--theme-primary)"
                onClick={() =>
                  setEditModeMap((prev) => ({
                    ...prev,
                    [activeNav]: "edit",
                  }))
                }
              >
                編輯
              </CustomButton>
            ) : (
              <>
                <CustomButton
                  bgColor="#fff"
                  color="#666"
                  style={{ border: "1px solid #d9d9d9" }}
                  onClick={() => {
                    if (activeNav === "profile") {
                      profileRef.current?.cancel();
                    }
                    setEditModeMap((prev) => ({ ...prev, [activeNav]: "view" }));
                    setDirtyMap((prev) => ({ ...prev, [activeNav]: false }));
                  }}
                >
                  取消
                </CustomButton>
                <CustomButton
                  bgColor="var(--theme-primary)"
                  onClick={async () => {
                    try {
                      if (activeNav === "profile") {
                        await profileRef.current?.save();
                      }
                      // 只有保存成功才重置状态
                      setEditModeMap((prev) => ({ ...prev, [activeNav]: "view" }));
                      setDirtyMap((prev) => ({ ...prev, [activeNav]: false }));
                    } catch (error) {
                      // 保存失败，保持编辑状态
                      console.error("Save failed:", error);
                    }
                  }}
                >
                  儲存
                </CustomButton>
              </>
            )}
          </Space>
        </div>
      )}
      <ZuuModeSwitch
        mode={isZuuMode ? "zuu" : "original"}
        onToggle={handleZuuModeToggle}
        bottomOffset={supportsEdit ? 96 : 24}
      />
    </div>
  );
}
